import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Redirect, useParams } from 'react-router-dom';
import Alert from 'Components/Alert';
import DescriptionList from 'Components/DescriptionList/DescriptionList';
import DescriptionListItem from 'Components/DescriptionList/DescriptionListItem';
import FieldSet from 'Components/FieldSet';
import CheckInput from 'Components/Form/CheckInput';
import FormGroup from 'Components/Form/FormGroup';
import FormInputHelpText from 'Components/Form/FormInputHelpText';
import FormLabel from 'Components/Form/FormLabel';
import SelectInput from 'Components/Form/SelectInput';
import TextInput from 'Components/Form/TextInput';
import Button from 'Components/Link/Button';
import ConfirmModal from 'Components/Modal/ConfirmModal';
import PageContent from 'Components/Page/PageContent';
import PageContentBody from 'Components/Page/PageContentBody';
import { kinds } from 'Helpers/Props';
import { tdarrCrud, tdarrPost } from '../api';
import SettingsToolbar from '../components/SettingsToolbar';
import { useTdarrCollection } from '../queries';
import { getThemePreference, setThemePreference, ThemePreference } from '../theme';
import { getAdvancedSettings, setAdvancedSettings, SettingsSectionId, settingsSections } from '../settings';

type OptionValue = boolean | number | string;
type FieldType = 'boolean' | 'number' | 'text' | 'password' | 'select';
interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  helpText?: string;
  advanced?: boolean;
  values?: Array<{ key: string; value: string }>;
  min?: number;
}
interface GroupDefinition { category: Exclude<SettingsSectionId, 'notifications' | 'ui'>; legend: string; fields: FieldDefinition[] }

const themeValues = [{ key: 'auto', value: 'Auto' }, { key: 'light', value: 'Light' }, { key: 'dark', value: 'Dark' }];
const languageValues = [{ key: 'en', value: 'English' }];
const toastValues = [
  { key: 'top-left', value: 'Top left' }, { key: 'top-center', value: 'Top center' }, { key: 'top-right', value: 'Top right' },
  { key: 'bottom-left', value: 'Bottom left' }, { key: 'bottom-center', value: 'Bottom center' }, { key: 'bottom-right', value: 'Bottom right' }
];
const queueSortValues = [
  ['noSort', 'No Sort'], ['sortDateOldest', 'Oldest (Scanned)'], ['sortDateNewest', 'Newest (Scanned)'],
  ['sortDateFileCreatedOldest', 'Oldest (Created)'], ['sortDateFileCreatedNewest', 'Newest (Created)'],
  ['sortDateFileModifiedOldest', 'Oldest (Modified)'], ['sortDateFileModifiedNewest', 'Newest (Modified)'],
  ['sortSizeSmallest', 'Smallest'], ['sortSizeLargest', 'Largest'], ['sortCodecAZ', 'Video Codec A-Z'], ['sortCodecZA', 'Video Codec Z-A'],
  ['sortAudioCodecAZ', 'Audio Codec A-Z'], ['sortAudioCodecZA', 'Audio Codec Z-A'], ['sortContainerAZ', 'Container A-Z'],
  ['sortContainerZA', 'Container Z-A'], ['sortFilenameAZ', 'Filename A-Z'], ['sortFilenameZA', 'Filename Z-A'],
  ['sortPathAZ', 'Path A-Z'], ['sortPathZA', 'Path Z-A'], ['sortBitrateSmallest', 'Bitrate smallest'],
  ['sortBitrateLargest', 'Bitrate largest'], ['sortResolutionSmallest', 'Resolution smallest'], ['sortResolutionLargest', 'Resolution largest'],
  ['sortDurationSmallest', 'Duration smallest'], ['sortDurationLargest', 'Duration largest']
].map(([key, value]) => ({ key, value }));

const groups: GroupDefinition[] = [
  { category: 'processing', legend: 'Staging', fields: [
    { key: 'autoAcceptTranscodes', label: 'Auto accept successful transcodes', type: 'boolean', helpText: 'Automatically accept successful transcodes waiting in the staging section.' },
    { key: 'logFullCliOutput', label: 'Log full FFmpeg/HandBrake output', type: 'boolean', helpText: 'Store full command output in job reports instead of only the final lines.', advanced: true },
    { key: 'stagedFileLimit', label: 'Staged file limit', type: 'number', min: 1, helpText: 'Maximum number of files allowed in the staging section.' }
  ] },
  { category: 'processing', legend: 'Queue', fields: [
    { key: 'ignoreSchedules', label: 'Ignore schedules', type: 'boolean' },
    { key: 'queueSortType', label: 'Sort queue by', type: 'select', values: queueSortValues },
    { key: 'enableBumpedFiles', label: 'Enable bumped files', type: 'boolean' },
    { key: 'alternateLibraries', label: 'Library alternation', type: 'boolean' },
    { key: 'prioritiseLibraries', label: 'Library prioritisation', type: 'boolean' },
    { key: 'prioritiseTranscodes', label: 'Prioritise transcodes', type: 'boolean' },
    { key: 'prioritiseHealthChecks', label: 'Prioritise health checks', type: 'boolean' },
    { key: 'transcodeWorkerLimit', label: 'Transcode worker limit', type: 'number', min: 0, advanced: true },
    { key: 'healthcheckWorkerLimit', label: 'Health check worker limit', type: 'number', min: 0, advanced: true }
  ] },
  { category: 'workers', legend: 'API & Access', fields: [
    { key: 'enableUnmappedNodes', label: 'Allow unmapped Nodes and source/cache file access through API', type: 'boolean', helpText: 'Allows Nodes without shared path mappings to transfer source and cache files through the server API.' }
  ] },
  { category: 'workers', legend: 'Worker Behavior', fields: [
    { key: 'runMkvpropedit', label: 'Run mkvpropedit on files before running plugins', type: 'boolean' },
    { key: 'softPauseAllNodes', label: 'Soft pause nodes', type: 'boolean', helpText: 'Paused Nodes may finish items already in the staging section.' },
    { key: 'dontCleanWorkerCacheFolder', label: "Don't clean worker cache folder", type: 'boolean', advanced: true },
    { key: 'workerStallDetector', label: 'Auto-cancel stalled workers', type: 'boolean' },
    { key: 'workerStallDetectorInterval', label: 'Stall check interval (s)', type: 'number', min: 1, advanced: true },
    { key: 'autoPauseIfCacheFull', label: 'Auto pause nodes if cache full', type: 'boolean' },
    { key: 'autoPauseIfCacheFullThreshold', label: 'Cache threshold (GB)', type: 'number', min: 0, advanced: true }
  ] },
  { category: 'general', legend: 'Tdarr Pro', fields: [
    { key: 'tdarrKey', label: 'Tdarr Pro Key', type: 'password', helpText: 'Optional license key supplied by Tdarr.' }
  ] },
  { category: 'general', legend: 'Plugins', fields: [
    { key: 'communityPluginRepo', label: 'Community plugins repository', type: 'text' },
    { key: 'pluginAutoUpdate', label: 'Auto-update plugins', type: 'boolean' },
    { key: 'pluginPinnedSha', label: 'Plugin repo version', type: 'text', helpText: 'Leave blank to follow the latest community-plugin revision.', advanced: true }
  ] },
  { category: 'general', legend: 'Updates', fields: [
    { key: 'autoUpdateServer', label: 'Auto update non-Docker server', type: 'boolean' },
    { key: 'autoUpdateServerVersion', label: 'Server update version', type: 'text', advanced: true },
    { key: 'autoUpdateNodes', label: 'Auto update/downgrade non-Docker nodes', type: 'boolean' },
    { key: 'killAllProcessesDuringUpdate', label: 'Force end all Tdarr module processes during restart/update', type: 'boolean', advanced: true }
  ] },
  { category: 'general', legend: 'Data & Storage', fields: [
    { key: 'logWorkerVerdictTranscodeNotRequired', label: "Log 'Transcode Not Required' items on graphs", type: 'boolean' },
    { key: 'openJobReportsInNewTab', label: 'Open job reports in new tab', type: 'boolean' },
    { key: 'holdCheckerInterval', label: 'Hold checker interval (s)', type: 'number', min: 1, advanced: true },
    { key: 'backupLimit', label: 'Backup limit', type: 'number', min: 1 },
    { key: 'jobHistorySizeLimitGB', label: 'Total job history size limit (GB)', type: 'number', min: 0 }
  ] },
  { category: 'general', legend: 'Logging', fields: [
    { key: 'verboseLogs', label: 'Verbose logs', type: 'boolean' },
    { key: 'logInternalApiRequestReceived', label: 'Log internal API requests received', type: 'boolean', advanced: true },
    { key: 'logInternalApiRequestSuccess', label: 'Log successful internal API requests', type: 'boolean', advanced: true },
    { key: 'logInternalApiRequestFail', label: 'Log failed internal API requests', type: 'boolean', advanced: true },
    { key: 'logInternalApiRequestPayload', label: 'Log internal API request payloads', type: 'boolean', advanced: true },
    { key: 'logExternalApiRequestReceived', label: 'Log external API requests received', type: 'boolean', advanced: true },
    { key: 'logExternalApiRequestSuccess', label: 'Log successful external API requests', type: 'boolean', advanced: true },
    { key: 'logExternalApiRequestFail', label: 'Log failed external API requests', type: 'boolean', advanced: true },
    { key: 'logExternalApiRequestPayload', label: 'Log external API request payloads', type: 'boolean', advanced: true }
  ] }
];

const resolutionNames = ['res480p', 'res576p', 'res720p', 'res1080p', 'res1440p', 'res4KUHD', 'resDCI4K', 'res8KUHD'] as const;
const resolutionLabels: Record<typeof resolutionNames[number], string> = {
  res480p: '480p', res576p: '576p', res720p: '720p', res1080p: '1080p', res1440p: '1440p', res4KUHD: '4K UHD', resDCI4K: 'DCI 4K', res8KUHD: '8K UHD'
};
type ResolutionDraft = Record<string, { widthMin: number; widthMax: number; heightMin: number; heightMax: number }>;

function initialDraft(global: Record<string, unknown>) {
  const next: Record<string, OptionValue> = {};
  groups.forEach((group) => group.fields.forEach((field) => {
    const value = global[field.key];
    next[field.key] = field.type === 'boolean' ? Boolean(value) : field.type === 'number' ? Number(value ?? 0) : String(value ?? '');
  }));
  next.language = String(global.language ?? 'en');
  next.toastNotificationPosition = String(global.toastNotificationPosition ?? 'top-right');
  return next;
}

function initialResolutions(global: Record<string, unknown>): ResolutionDraft {
  const source = (global.resBoundaries ?? {}) as ResolutionDraft;
  return Object.fromEntries(resolutionNames.map((name) => [name, {
    widthMin: Number(source[name]?.widthMin ?? 0), widthMax: Number(source[name]?.widthMax ?? 0),
    heightMin: Number(source[name]?.heightMin ?? 0), heightMax: Number(source[name]?.heightMax ?? 0)
  }]));
}

export default function Options() {
  const { category } = useParams<{ category: string }>();
  const section = settingsSections.find((item) => item.id === category);
  const queryClient = useQueryClient();
  const [theme, setTheme] = useState(getThemePreference());
  const [savedTheme, setSavedTheme] = useState(getThemePreference());
  const [advanced, setAdvanced] = useState(getAdvancedSettings);
  const settings = useTdarrCollection('SettingsGlobalJSONDB');
  const global = settings.data?.[0];
  const [draft, setDraft] = useState<Record<string, OptionValue>>({});
  const [savedDraft, setSavedDraft] = useState<Record<string, OptionValue>>({});
  const [resolutions, setResolutions] = useState<ResolutionDraft>({});
  const [savedResolutions, setSavedResolutions] = useState<ResolutionDraft>({});
  const [actionMessage, setActionMessage] = useState<{ kind: 'success' | 'danger'; text: string } | null>(null);
  const [confirmRestart, setConfirmRestart] = useState(false);
  const activeGroups = groups.filter((group) => group.category === category);

  useEffect(() => {
    if (!global) return;
    const next = initialDraft(global);
    const nextResolutions = initialResolutions(global);
    setDraft(next);
    setSavedDraft(next);
    setResolutions(nextResolutions);
    setSavedResolutions(nextResolutions);
  }, [global]);

  const hasPendingChanges = useMemo(() => {
    const settingsChanged = JSON.stringify(draft) !== JSON.stringify(savedDraft);
    const resolutionChanged = category === 'general' && JSON.stringify(resolutions) !== JSON.stringify(savedResolutions);
    const themeChanged = category === 'ui' && theme !== savedTheme;
    return settingsChanged || resolutionChanged || themeChanged;
  }, [category, draft, resolutions, savedDraft, savedResolutions, theme, savedTheme]);

  const toggleAdvanced = () => setAdvanced((current) => {
    setAdvancedSettings(!current);
    return !current;
  });

  const save = useMutation({
    mutationFn: () => tdarrCrud({
      collection: 'SettingsGlobalJSONDB', mode: 'update', docID: 'globalsettings',
      obj: { ...draft, ...(category === 'general' ? { resBoundaries: resolutions } : {}) }
    }),
    onSuccess: async () => {
      setSavedDraft(draft);
      if (category === 'general') setSavedResolutions(resolutions);
      if (category === 'ui') {
        setThemePreference(theme);
        setSavedTheme(theme);
      }
      await queryClient.invalidateQueries({ queryKey: ['tdarr', 'collection', 'SettingsGlobalJSONDB'] });
    }
  });
  const action = useMutation({
    mutationFn: ({ path, data }: { path: string; data?: unknown; success: string }) => tdarrPost(path, data ?? { data: {} }),
    onSuccess: (_data, request) => {
      setActionMessage({ kind: 'success', text: request.success });
      queryClient.invalidateQueries({ queryKey: ['tdarr'] });
    },
    onError: () => setActionMessage({ kind: 'danger', text: 'The Tdarr server action failed.' })
  });

  if (!section || category === 'notifications') return <Redirect to="/settings" />;

  const renderField = (field: FieldDefinition) => {
    if (!advanced && field.advanced) return null;
    const update = (value: OptionValue) => setDraft((current) => ({ ...current, [field.key]: value }));
    return <FormGroup key={field.key} isAdvanced={field.advanced} advancedSettings={advanced}>
      <FormLabel name={field.key}>{field.label}</FormLabel>
      <div>
        {field.type === 'boolean' && <CheckInput name={field.key} value={Boolean(draft[field.key])} helpText={field.helpText} onChange={({ value }) => update(Boolean(value))} />}
        {field.type === 'number' && <TextInput<number> name={field.key} type="number" min={field.min} value={Number(draft[field.key] ?? 0)} onChange={({ value }) => update(Number(value))} />}
        {(field.type === 'text' || field.type === 'password') && <TextInput<string> name={field.key} type={field.type} value={String(draft[field.key] ?? '')} onChange={({ value }) => update(String(value))} />}
        {field.type === 'select' && <SelectInput<string> name={field.key} value={String(draft[field.key] ?? '')} values={field.values ?? []} onChange={({ value }) => update(String(value))} />}
        {field.helpText && field.type !== 'boolean' && <FormInputHelpText text={field.helpText} />}
      </div>
    </FormGroup>;
  };

  return <PageContent title={`${section.title} Settings`}>
    <SettingsToolbar advancedSettings={advanced} onAdvancedSettingsPress={toggleAdvanced} isSaving={save.isPending} hasPendingChanges={hasPendingChanges} isSaveDisabled={!global} onSavePress={() => save.mutate()} />
    <PageContentBody>
      {settings.isError && <Alert kind={kinds.DANGER}>Unable to load global Tdarr settings.</Alert>}
      {save.isSuccess && !hasPendingChanges && <Alert kind={kinds.SUCCESS}>Settings saved.</Alert>}
      {save.isError && <Alert kind={kinds.DANGER}>Settings could not be saved.</Alert>}
      {actionMessage && <Alert kind={actionMessage.kind}>{actionMessage.text}</Alert>}

      {category === 'ui' && <FieldSet legend="Appearance">
        <FormGroup><FormLabel name="theme">Theme</FormLabel><div><SelectInput<ThemePreference> name="theme" value={theme} values={themeValues} onChange={({ value }) => setTheme(value)} /><FormInputHelpText text="Auto follows your operating system. Theme changes are applied when you save." /></div></FormGroup>
        <FormGroup><FormLabel name="language">Language</FormLabel><SelectInput<string> name="language" value={String(draft.language ?? 'en')} values={languageValues} onChange={({ value }) => setDraft((current) => ({ ...current, language: String(value) }))} /></FormGroup>
        <FormGroup><FormLabel name="toastNotificationPosition">Toast notification position</FormLabel><SelectInput<string> name="toastNotificationPosition" value={String(draft.toastNotificationPosition ?? 'top-right')} values={toastValues} onChange={({ value }) => setDraft((current) => ({ ...current, toastNotificationPosition: String(value) }))} /></FormGroup>
      </FieldSet>}

      {activeGroups.map((group) => <FieldSet key={group.legend} legend={group.legend}>
        {group.fields.map(renderField)}
        {group.legend === 'Plugins' && global && <DescriptionList>
          <DescriptionListItem title="Current plugin revision" data={String(global.pluginCurrentSha ?? '—').slice(0, 8)} />
          <DescriptionListItem title="Latest known revision" data={String(global.pluginLatestKnownSha ?? '—').slice(0, 8)} />
        </DescriptionList>}
        {group.legend === 'Plugins' && <div className="tdarr-settings-actions"><Button isDisabled={action.isPending} onPress={() => action.mutate({ path: '/update-plugins', data: { data: { force: true } }, success: 'Community plugin update requested.' })}>Update Community Plugins</Button><Button isDisabled={action.isPending} onPress={() => action.mutate({ path: '/sync-plugins', success: 'Node plugin sync requested.' })}>Sync Node Plugins</Button></div>}
        {group.legend === 'Updates' && <div className="tdarr-settings-actions"><Button isDisabled={action.isPending} onPress={() => action.mutate({ path: '/updater/check', data: { downloadUpdate: false, applyUpdate: false, resetUpdater: false }, success: 'Update check completed.' })}>Check For Update</Button></div>}
      </FieldSet>)}

      {category === 'general' && <FieldSet legend="Resolution Boundaries">
        <div className="tdarr-resolution-grid tdarr-resolution-grid-header"><span>Resolution</span><span>Width Min</span><span>Width Max</span><span>Height Min</span><span>Height Max</span></div>
        {resolutionNames.map((name) => <div className="tdarr-resolution-grid" key={name}>
          <strong>{resolutionLabels[name]}</strong>
          {(['widthMin', 'widthMax', 'heightMin', 'heightMax'] as const).map((property) => <TextInput<number> key={property} name={`${name}.${property}`} type="number" min={0} value={resolutions[name]?.[property] ?? 0} onChange={({ value }) => setResolutions((current) => ({ ...current, [name]: { ...current[name], [property]: Number(value) } }))} />)}
        </div>)}
      </FieldSet>}

      {category === 'general' && global && <FieldSet legend="Server Information"><DescriptionList>
        <DescriptionListItem title="Process Priority" data={String(global.processPriority ?? 'normal')} />
        <DescriptionListItem title="Server Engine" data={String(global.serverEngine ?? '—')} />
        <DescriptionListItem title="Running in Docker" data={global.runningDocker ? 'Yes' : 'No'} />
      </DescriptionList></FieldSet>}
      {category === 'general' && <FieldSet legend="Server Actions"><div className="tdarr-settings-actions"><Button kind={kinds.DANGER} isDisabled={action.isPending} onPress={() => setConfirmRestart(true)}>Restart Server</Button><Button isDisabled={action.isPending} onPress={() => action.mutate({ path: '/updater/check', data: { downloadUpdate: false, applyUpdate: false, resetUpdater: true }, success: 'Updater state reset.' })}>Reset Updater</Button></div></FieldSet>}
    </PageContentBody>
    <ConfirmModal isOpen={confirmRestart} kind="danger" title="Restart Server" message="Restart the Tdarr Server? Active operations may be interrupted while it reconnects." confirmLabel="Restart" isSpinning={action.isPending} onConfirm={() => { setConfirmRestart(false); action.mutate({ path: '/restart-server', success: 'Server restart requested.' }); }} onCancel={() => setConfirmRestart(false)} />
  </PageContent>;
}
